import type { RouteLocationNormalized } from '#vue-router'
import type {
  AddToCartPayloadArgs,
  CarouselClickPayloadArgs,
  FilterClickPayloadArgs,
  MenuPayloadArgs,
  NewsletterSignupPayloadArgs,
  PageBlockClickPayloadArgs,
  PopupPayloadArgs,
  ProductClickPayloadArgs,
  ProductDetailPayloadArgs,
  ProductImpressionPayloadArgs,
  ProductPagePayloadArgs,
  PromoPayloadArgs,
} from 'lib/gtm/payloads'
import type { CheckoutView } from 'types/checkout'
import type { BaseGTMEvent, GTMEvent, GTMEventName, searchAction } from 'types/gtm'
import type { CartItem } from 'types/models/cart.model'
import {
  addToCartPayload,
  cartUpdatePayload,
  carouselClickPayload,
  checkoutCustomerEmailUpdatePayload,
  checkoutViewPayload,
  clickSearchPayload,
  closeProductPayload,
  filterClickPayload,
  generalInfoPayload,
  menuPayload,
  newsletterSignupPayload,
  orderCompletePayload,
  orderDiscountCodePayload,
  pageBlockClickPayload,
  paymentCancelPayload,
  popupPayload,
  productClickPayload,
  productDetailPayload,
  productImpressionPayload,
  productPagePayload,
  promoPayload,
  purchasePayload,
  removeFromCartPayload,
  spaPageViewPayload,
} from 'lib/gtm/payloads'
import { getActiveSplitTests } from '~/configuration/split-testing.configuration'
import { getSplitTestVariants, processCartData } from '~/lib/gtm/helpers'

let timeout: ReturnType<typeof setTimeout> | undefined

const PosthogKeyEvents: Partial<GTMEventName>[] = ['general', 'purchase']

function isKeyEvent(eventName: GTMEventName): boolean {
  return PosthogKeyEvents.includes(eventName)
}

function isEventInSplitTest(event: GTMEvent): boolean {
  const storefrontStore = useStorefrontStore()
  const marketCode = storefrontStore.currentMarketCountryCode
  const activeSplitTests = getActiveSplitTests(marketCode)

  if (!activeSplitTests.length) {
    return false
  }

  const eventName = event.event as GTMEventName

  if (isKeyEvent(eventName)) {
    return true
  }

  const isSplitTestEvent = activeSplitTests.some((test) => {
    const testEventNames = test.gtmEventNames || []
    return testEventNames.includes(eventName)
  })

  if (isSplitTestEvent) {
    return true
  }

  return false
}

function push(data: GTMEvent) {
  const inSplitTest = isEventInSplitTest(data)

  const enrichedData: BaseGTMEvent & GTMEvent = {
    ...data,
    inSplitTest,
    splitTests: getSplitTestVariants(),
  }

  window.dataLayer?.push(enrichedData)
}

export function useGTM() {
  const orderStore = useOrderStore()
  const { checkoutSteps, impressions } = storeToRefs(useCheckoutStore())

  const pushAddToCart = (args: AddToCartPayloadArgs) => {
    push(addToCartPayload(args))
  }

  const pushRemoveFromCart = (item: CartItem, itemQty: number) => {
    push(removeFromCartPayload(item, itemQty))
  }

  const pushPaymentCancelEvent = () => {
    push(paymentCancelPayload())
  }

  const pushCartUpdate = () => {
    push(cartUpdatePayload())
  }

  const pushPurchaseEvent = () => {
    if (!orderStore.session)
      throw new ReferenceError(`Could not find a session or order`)

    const {
      session: { order },
    } = orderStore

    push(purchasePayload(order))
    push(orderCompletePayload(order))
  }

  const pushProductPurchaseEvent = () => {
    if (!orderStore.session)
      throw new ReferenceError(`Could not find a session or order`)

    const {
      session: { order },
    } = orderStore

    const items = processCartData(order.items, order.currency)

    for (const item of items) {
      push({
        event: 'productPurchase',
        ecommerce: {
          productPurchase: {
            product: item,
          },
        },
      })
    }
  }

  const pushOrderDiscountCode = (discountCode: string) => {
    push(orderDiscountCodePayload(discountCode))
  }

  const pushCheckoutView = (view: CheckoutView) => {
    const steps: Record<CheckoutView, number> = {
      cart: 1,
      address: 2,
      payment: 3,
      success: 4,
    }

    push(checkoutViewPayload(steps, view))
    if (steps[view] === 4)
      checkoutSteps.value = []
  }

  const pushCheckoutCustomerEmailUpdate = (email: string) => {
    push(checkoutCustomerEmailUpdatePayload(email))
  }

  const pushProductPageEvent = (data: ProductPagePayloadArgs) => {
    push(productPagePayload(data))
  }

  const pushProductDetail = (data: ProductDetailPayloadArgs) => {
    push(productDetailPayload(data))
  }

  const pushCloseProduct = (pageUrl: string) => {
    push(closeProductPayload(pageUrl))
  }

  const pushProductClick = (data: ProductClickPayloadArgs) => {
    push(productClickPayload(data))
  }

  const pushProductImpression = (data: Omit<ProductImpressionPayloadArgs, 'impressions'>) => {
    impressions.value.push(data.product)
    if (timeout)
      return
    timeout = setTimeout(() => {
      push(productImpressionPayload({ ...data, impressions: impressions.value }))
      timeout = undefined
      impressions.value = []
    }, 500)
  }

  const pushGeneralInfo = (pageType: string, url: string) => {
    push(generalInfoPayload(pageType, url))
  }

  const pushNewsletterEvent = (args: NewsletterSignupPayloadArgs) => {
    push(newsletterSignupPayload(args))
  }

  const pushPromoClick = (promo: PromoPayloadArgs) => {
    push(promoPayload('promotionClick', promo))
  }
  const pushPromoView = (promo: PromoPayloadArgs) => {
    push(promoPayload('promotionView', promo))
  }

  const pushClickSearch = (searchTerm: string, sku: string, action: searchAction) => {
    push(clickSearchPayload(searchTerm, sku, action))
  }

  const pushPageBlockClick = (args: PageBlockClickPayloadArgs) => {
    push(pageBlockClickPayload(args))
  }

  const pushFilterClick = (args: FilterClickPayloadArgs) => {
    push(filterClickPayload(args))
  }

  const pushSpaPageView = (to: RouteLocationNormalized) => {
    push(spaPageViewPayload(to))
  }

  function pushPopup(payload: PopupPayloadArgs) {
    push(popupPayload(payload))
  }

  function pushMenuClick(payload: MenuPayloadArgs) {
    push(menuPayload(payload))
  }

  function pushCarouselClick(payload: CarouselClickPayloadArgs) {
    push(carouselClickPayload(payload))
  }

  return {
    push,
    pushAddToCart,
    pushRemoveFromCart,
    pushPaymentCancelEvent,
    pushCartUpdate,
    pushCarouselClick,
    pushPurchaseEvent,
    pushProductPurchaseEvent,
    pushOrderDiscountCode,
    pushCheckoutView,
    pushCheckoutCustomerEmailUpdate,
    pushProductPageEvent,
    pushProductDetail,
    pushCloseProduct,
    pushProductClick,
    pushProductImpression,
    pushGeneralInfo,
    pushNewsletterEvent,
    pushPromoClick,
    pushPromoView,
    pushClickSearch,
    pushPageBlockClick,
    pushFilterClick,
    pushSpaPageView,
    pushPopup,
    pushMenuClick,
  }
}
